import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddReferralDialog } from '../../components/admin/AddReferralDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Download, Eye, MapPin, Award, Copy, Link, Trash2, UserPlus, Upload, FileSpreadsheet, CreditCard, Search, MessageSquare } from 'lucide-react';
import { LogOutcomeDialog } from '@/components/admin/LogOutcomeDialog';
import { outcomeLabel } from '@/components/admin/outcomeApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Use Shopify myshopify.com domain for discount permalink to auto-apply
const SHOPIFY_STORE_DOMAIN = 'kilobeaters-in.myshopify.com';

// Generate proper Shopify discount permalink for auto-apply
const getReferralLink = (referralCode) => {
  if (!referralCode) return '';
  return `https://${SHOPIFY_STORE_DOMAIN}/discount/${referralCode}`;
};

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [addPartnerDialogOpen, setAddPartnerDialogOpen] = useState(false);
  const [createdPartnerDialogOpen, setCreatedPartnerDialogOpen] = useState(false);
  const [createdPartner, setCreatedPartner] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedPartnerForUpload, setSelectedPartnerForUpload] = useState(null);
  const [addReferralPartner, setAddReferralPartner] = useState(null);
  const [outcomePartner, setOutcomePartner] = useState(null);
  const [bankDetailsDialogOpen, setBankDetailsDialogOpen] = useState(false);
  const [selectedPartnerBank, setSelectedPartnerBank] = useState(null);
  const [newPartner, setNewPartner] = useState({ name: '', email: '', phone: '', city: '' });
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Filter partners based on search query and engagement status
  const filteredPartners = partners.filter(partner => {
    // Engagement status filter
    if (statusFilter !== 'all' && partner.engagement_status !== statusFilter) return false;

    // Location filter (case-insensitive; blank = "unset")
    if (locationFilter !== 'all') {
      const loc = (partner.location || '').toLowerCase();
      if (locationFilter === 'unset' && loc) return false;
      if (locationFilter !== 'unset' && loc !== locationFilter.toLowerCase()) return false;
    }
    
    // Search filter
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (partner.name || '').toLowerCase().includes(query) ||
      (partner.email || '').toLowerCase().includes(query) ||
      (partner.phone || '').toLowerCase().includes(query) ||
      (partner.city || '').toLowerCase().includes(query) ||
      (partner.referral_code || '').toLowerCase().includes(query)
    );
  });

  const handleBankDetailsClick = (partner) => {
    setSelectedPartnerBank(partner);
    setBankDetailsDialogOpen(true);
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await fetch(`${API}/admin/partners`, {
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to fetch partners');
      
      const data = await response.json();
      setPartners(data);
    } catch (error) {
      toast.error('Failed to load partners');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updatePartnerStatus = async (partnerId, status) => {
    try {
      const response = await fetch(`${API}/admin/partners/${partnerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({ status })
      });
      
      if (!response.ok) throw new Error('Failed to update partner');
      
      toast.success('Partner status updated');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to update partner status');
    }
  };

  const handleDeleteClick = (partner) => {
    setPartnerToDelete(partner);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!partnerToDelete) return;
    
    try {
      const response = await fetch(`${API}/admin/partners/${partnerToDelete.user_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete partner');
      }
      
      toast.success(`Partner "${partnerToDelete.name}" deleted successfully`);
      fetchPartners();
    } catch (error) {
      toast.error(error.message || 'Failed to delete partner');
    } finally {
      setDeleteDialogOpen(false);
      setPartnerToDelete(null);
    }
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    if (!newPartner.name || !newPartner.email) {
      toast.error('Name and email are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/admin/partners/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(newPartner)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create partner');
      }
      
      const data = await response.json();
      // Show the created partner details dialog
      setCreatedPartner({
        name: data.partner.name,
        email: data.partner.email,
        referral_code: data.partner.referral_code,
        referral_link: getReferralLink(data.partner.referral_code)
      });
      setAddPartnerDialogOpen(false);
      setCreatedPartnerDialogOpen(true);
      setNewPartner({ name: '', email: '', phone: '', city: '' });
      fetchPartners();
    } catch (error) {
      toast.error(error.message || 'Failed to create partner');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const handleUploadClick = (partner) => {
    setSelectedPartnerForUpload(partner);
    setUploadDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedPartnerForUpload) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/admin/referrals/upload/${selectedPartnerForUpload.user_id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload referrals');
      }
      
      const data = await response.json();
      toast.success(data.message);
      
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach(err => toast.error(err));
      }
      
      setUploadDialogOpen(false);
      setSelectedPartnerForUpload(null);
    } catch (error) {
      toast.error(error.message || 'Failed to upload referrals');
    } finally {
      setSubmitting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    window.open(`${API}/admin/referrals/template?token=${localStorage.getItem('session_token') || sessionStorage.getItem('session_token')}`, '_blank');
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'platinum':
        return 'bg-purple-100 text-purple-700';
      case 'gold':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Partners
          </h1>
          <p className="text-muted-foreground">Manage partner accounts and view profiles</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={downloadTemplate}
            variant="outline"
            data-testid="download-template-button"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Download Template
          </Button>
          <Dialog open={addPartnerDialogOpen} onOpenChange={setAddPartnerDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-partner-button">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Partner
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Partner</DialogTitle>
                <DialogDescription>
                  Create a new partner account. A referral code will be automatically generated.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddPartner} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={newPartner.name}
                    onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                    placeholder="Partner name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newPartner.email}
                    onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
                    placeholder="partner@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={newPartner.phone}
                    onChange={(e) => setNewPartner({ ...newPartner, phone: e.target.value })}
                    placeholder="9876543210"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={newPartner.city}
                    onChange={(e) => setNewPartner({ ...newPartner, city: e.target.value })}
                    placeholder="Mumbai"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setAddPartnerDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Partner'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Created Partner Success Dialog */}
          <Dialog open={createdPartnerDialogOpen} onOpenChange={setCreatedPartnerDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-green-600">✓ Partner Created Successfully!</DialogTitle>
                <DialogDescription>
                  Share these details with the partner. They can sign in using their email to access the portal.
                </DialogDescription>
              </DialogHeader>
              {createdPartner && (
                <div className="space-y-4">
                  <div className="p-4 bg-muted rounded-lg space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Partner Name</p>
                      <p className="font-medium">{createdPartner.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{createdPartner.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Referral Code</p>
                      <div className="flex items-center gap-2">
                        <code className="bg-background px-2 py-1 rounded font-mono text-primary">{createdPartner.referral_code}</code>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => copyToClipboard(createdPartner.referral_code, 'Referral code')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Referral Link</p>
                      <div className="flex items-center gap-2">
                        <code className="bg-background px-2 py-1 rounded text-xs break-all">{createdPartner.referral_link}</code>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => copyToClipboard(createdPartner.referral_link, 'Referral link')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Note:</strong> A welcome email has been sent to {createdPartner.email} with their referral code and login instructions.
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => setCreatedPartnerDialogOpen(false)}>
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filter UI */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search partners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-partners-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Partner Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Partners</SelectItem>
            <SelectItem value="has_transactions">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Referrals Transacting
              </div>
            </SelectItem>
            <SelectItem value="added_referrals">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                Referrals Added
              </div>
            </SelectItem>
            <SelectItem value="logged_in_only">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                No Referrals Added
              </div>
            </SelectItem>
            <SelectItem value="dormant">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                No Login (30+ days)
              </div>
            </SelectItem>
            <SelectItem value="never_logged_in">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Never Logged In
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-40" data-testid="location-filter-select">
            <SelectValue placeholder="Location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            <SelectItem value="India">🇮🇳 India</SelectItem>
            <SelectItem value="Dubai">🇦🇪 Dubai</SelectItem>
            <SelectItem value="unset">Not set</SelectItem>
          </SelectContent>
        </Select>
        {(searchQuery || statusFilter !== 'all' || locationFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearchQuery(''); setStatusFilter('all'); setLocationFilter('all'); }}
            data-testid="clear-search-button"
          >
            Clear
          </Button>
        )}
      </div>

      <div className="flex justify-end mb-4">
        <Button 
          onClick={async () => {
            try {
              toast.info('Preparing export...');
              const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
              if (!sessionToken) {
                toast.error('Not authenticated. Please login again.');
                return;
              }
              
              const result = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('GET', `${API}/admin/partners/export`, true);
                xhr.responseType = 'blob';
                xhr.timeout = 60000;
                xhr.setRequestHeader('Authorization', `Bearer ${sessionToken}`);
                
                xhr.onload = function() {
                  if (xhr.status >= 200 && xhr.status < 300) {
                    resolve({ ok: true, blob: xhr.response });
                  } else {
                    resolve({ ok: false, status: xhr.status });
                  }
                };
                xhr.onerror = () => reject(new Error('Network error'));
                xhr.ontimeout = () => reject(new Error('Request timed out'));
                xhr.send();
              });
              
              if (result.ok) {
                const downloadUrl = window.URL.createObjectURL(result.blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `KiloBeaters_Partners_${new Date().toISOString().slice(0,10)}.xlsx`;
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(downloadUrl);
                }, 100);
                toast.success('Partners exported successfully');
              } else {
                toast.error('Failed to export partners');
              }
            } catch (error) {
              toast.error('Export failed: ' + error.message);
            }
          }}
          variant="outline"
          data-testid="export-partners-button"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {searchQuery ? `Filtered Partners (${filteredPartners.length} of ${partners.length})` : `All Partners (${partners.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredPartners.length === 0 ? (
              <div className="text-center py-8">
                <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery ? `No partners found matching "${searchQuery}"` : 'No partners found'}
                </p>
                {searchQuery && (
                  <Button
                    variant="link"
                    onClick={() => setSearchQuery('')}
                    className="mt-2"
                  >
                    Clear search
                  </Button>
                )}
              </div>
            ) : (
              filteredPartners.map((partner) => (
              <div
                key={partner.user_id}
                data-testid={`partner-${partner.user_id}`}
                className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={partner.picture} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {partner.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{partner.name}</p>
                      {partner.star_rating ? (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs font-semibold"
                          title={`Influence rating: ${partner.star_rating}/5`}
                          data-testid={`partner-rating-${partner.user_id}`}
                        >
                          ⭐ {partner.star_rating}
                        </span>
                      ) : null}
                      <Badge className={getTierColor(partner.tier || 'silver')} variant="outline">
                        <Award className="h-3 w-3 mr-1" />
                        {partner.tier?.toUpperCase() || 'SILVER'}
                      </Badge>
                      <Badge className={`${
                        partner.engagement_status === 'has_transactions' ? 'bg-green-100 text-green-700 border-green-200' :
                        partner.engagement_status === 'added_referrals' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                        partner.engagement_status === 'logged_in_only' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                        partner.engagement_status === 'dormant' ? 'bg-gray-100 text-gray-600 border-gray-200' :
                        partner.engagement_status === 'never_logged_in' ? 'bg-red-100 text-red-700 border-red-200' :
                        'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        {partner.engagement_status === 'has_transactions' ? 'Referrals Transacting' :
                         partner.engagement_status === 'added_referrals' ? 'Referrals Added' :
                         partner.engagement_status === 'logged_in_only' ? 'No Referrals' :
                         partner.engagement_status === 'dormant' ? 'No Login 30+ Days' :
                         partner.engagement_status === 'never_logged_in' ? 'Never Logged In' : 'Unknown'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{partner.email}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      {partner.location && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-[11px] font-medium" data-testid={`partner-location-${partner.user_id}`}>
                          {partner.location === 'Dubai' ? '🇦🇪' : '🇮🇳'} {partner.location}
                        </span>
                      )}
                      {partner.city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {partner.city}
                        </span>
                      )}
                      <span>{partner.referral_count} referrals</span>
                      <span>Joined {new Date(partner.created_at).toLocaleDateString()}</span>
                    </div>
                    {partner.latest_outcome && (
                      <p className="text-xs mt-1 text-orange-700 truncate max-w-md" data-testid={`partner-last-outcome-${partner.user_id}`}
                        title={partner.latest_outcome.note || ''}>
                        Last: <strong>{outcomeLabel(partner.latest_outcome.outcome_type)}</strong>
                        {' · '}{new Date(partner.latest_outcome.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                        {partner.latest_outcome.note && <span className="text-muted-foreground"> — "{partner.latest_outcome.note}"</span>}
                      </p>
                    )}
                    {/* Referral Code & Link */}
                    {partner.referral_code && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="font-mono text-sm font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                          {partner.referral_code}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => copyToClipboard(partner.referral_code, 'Referral code')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => copyToClipboard(getReferralLink(partner.referral_code), 'Referral link')}
                        >
                          <Link className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={partner.status === 'active' ? 'default' : 'secondary'}>
                    {partner.status}
                  </Badge>
                  <Select
                    value={partner.status}
                    onValueChange={(value) => updatePartnerStatus(partner.user_id, value)}
                  >
                    <SelectTrigger data-testid={`status-select-${partner.user_id}`} className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    size="sm"
                    onClick={() => setOutcomePartner(partner)}
                    data-testid={`log-outcome-${partner.user_id}`}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    title="Log a call outcome or comment"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Log outcome
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAddReferralPartner(partner)}
                    data-testid={`add-referral-${partner.user_id}`}
                    title="Add a single referral"
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Referral
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleUploadClick(partner)}
                    data-testid={`upload-referrals-${partner.user_id}`}
                    title="Upload referrals"
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleBankDetailsClick(partner)}
                    data-testid={`bank-details-${partner.user_id}`}
                    title="View bank details"
                  >
                    <CreditCard className="h-4 w-4 mr-1" />
                    Bank
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/admin/partners/${partner.user_id}`)}
                    data-testid={`view-partner-${partner.user_id}`}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteClick(partner)}
                    data-testid={`delete-partner-${partner.user_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
            )}
          </div>
        </CardContent>
      </Card>

      <LogOutcomeDialog
        open={!!outcomePartner}
        onOpenChange={(o) => !o && setOutcomePartner(null)}
        partner={outcomePartner}
        onSaved={(_, payload) => {
          setPartners(ps => ps.map(p => p.user_id !== payload.partner_id ? p : {
            ...p,
            star_rating: payload.star_rating || p.star_rating,
            latest_outcome: { outcome_type: payload.outcome_type, note: payload.note || '', created_at: new Date().toISOString() },
          }));
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Partner</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{partnerToDelete?.name}</strong>? 
              This action cannot be undone. All associated referrals, payout requests, 
              and session data will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Partner
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Referrals Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Referrals for {selectedPartnerForUpload?.name}</DialogTitle>
            <DialogDescription>
              Upload an Excel file with referral information. Use the template for the correct format.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 mb-2">Select an Excel file (.xlsx)</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="referral-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                {submitting ? 'Uploading...' : 'Choose File'}
              </Button>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Need the template?</span>
              <Button variant="link" onClick={downloadTemplate} className="p-0 h-auto">
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                Download Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={bankDetailsDialogOpen} onOpenChange={setBankDetailsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Bank Details - {selectedPartnerBank?.name}
            </DialogTitle>
            <DialogDescription>
              Bank account information for payout processing
            </DialogDescription>
          </DialogHeader>
          {selectedPartnerBank && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Bank Name</p>
                  <p className="font-medium">{selectedPartnerBank.bank_name || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Account Holder</p>
                  <p className="font-medium">{selectedPartnerBank.account_holder || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Account Number</p>
                  <p className="font-medium font-mono">
                    {selectedPartnerBank.account_number || 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">IFSC Code</p>
                  <p className="font-medium font-mono">{selectedPartnerBank.ifsc_code || 'Not provided'}</p>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p>{selectedPartnerBank.email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Phone</p>
                    <p>{selectedPartnerBank.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>
              {!selectedPartnerBank.bank_name && !selectedPartnerBank.account_number && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  This partner has not added their bank details yet. They can update this in their Profile page.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Single-add referral dialog (admin) */}
      <AddReferralDialog
        open={!!addReferralPartner}
        onOpenChange={(o) => { if (!o) setAddReferralPartner(null); }}
        partner={addReferralPartner}
        onAdded={() => fetchPartners()}
      />
    </div>
  );
}
